import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import {
  useForm,
  useFormContext,
  Controller,
  FormProvider,
} from "react-hook-form";

import Fade from "@material-ui/core/Fade";
import Modal, { ModalContent, ModalActions } from "lib/components/Modal";
import Spin from "lib/components/Spin";
import Button from "lib/components/Button";
import Stack from "lib/components/Stack";
import TextField from "lib/components/TextField";
import IconClose from "lib/assets/close-modal.svg";
import IconHashtag from "lib/assets/hashtag.svg";
import IconTimeline from "lib/assets/timeline.svg";
import IconDelete from "lib/assets/close.svg";
import IconArrowDown from "lib/assets/arrow-down.svg";
import { useCommunity } from "domain/community/communityContext";
import { useTheme } from "lib/contexts/themeContext";
import useIsResMobile from "lib/hooks/useIsResMobile";
import { CustomEventLabel } from "lib/enums";
import { useEvent } from "lib/contexts/eventContext";
import useDataTimeline from "lib/hooks/useDataTimeline";

interface CreateEditPostModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    postId?: number;
    categoryId?: number;
    title: string;
    contents: string;
  }) => Promise<void>;
  defaultValues?: {
    categoryId: number;
    title: string;
    contents: string;
  };
  postId?: number;
  category?: number;
}

const CreateEditPostModal: React.FC<CreateEditPostModalProps> = ({
  open,
  onClose,
  onSubmit,
  postId,
  defaultValues,
}) => {
  const isResMobile = useIsResMobile();
  return (
    <Modal open={open} onClose={onClose} fullScreen={isResMobile}>
      <CreateEditPostModalContents
        postId={postId}
        defaultValues={defaultValues}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default CreateEditPostModal;

const CreateEditPostModalContents = ({
  postId,
  defaultValues,
  onClose,
  onSubmit,
}) => {
  const {
    state: { dataListCommunityCategory },
  } = useCommunity();
  const [step, setStep] = useState(defaultValues ? 1 : 0);
  const methods = useForm<{
    categoryId: number;
    title: string;
    contents: string;
  }>({
    defaultValues: {
      categoryId: defaultValues?.categoryId ?? null,
      title: defaultValues?.title || "",
      contents: defaultValues?.contents || "",
    },
    // Preserve form state across steps.
    shouldUnregister: false,
  });
  const categoryId = methods.watch("categoryId");

  const handleOnBack = useCallback(() => {
    setStep(0);
    methods.setValue("categoryId", null);
  }, [methods]);

  useEffect(() => {
    if (typeof categoryId === "number") {
      setStep(1);
    }
  }, [categoryId]);

  return (
    <div>
      <ModalContent>
        {/* Title */}
        <Header step={step} onClose={onClose} />

        {/* Contents */}
        {dataListCommunityCategory.length > 1 ? (
          <FormProvider {...methods}>
            {step === 0 ? (
              <Controller
                render={({ onChange }) => (
                  <SelectCategory onChange={onChange} />
                )}
                control={methods.control}
                name="categoryId"
                rules={{ required: true }}
              />
            ) : (
              <WritePost
                postId={postId}
                categoryId={categoryId}
                onBack={handleOnBack}
                onSubmit={onSubmit}
                onClose={onClose}
              />
            )}
          </FormProvider>
        ) : (
          <Loading />
        )}
      </ModalContent>
    </div>
  );
};

const SelectCategory = ({ onChange }) => {
  const { breakpoints } = useTheme();
  const {
    state: { dataListCommunityCategory },
  } = useCommunity();

  const finalListCategory = useMemo(() => {
    return dataListCommunityCategory.filter(
      (category) => category.isReadOnlyCategory === false,
    );
  }, [dataListCommunityCategory]);

  return (
    <div
      css={`
        padding-bottom: 15px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 10px;
        ${breakpoints.down("sm")} {
          margin: 0 -10px;
          grid-template-columns: 100%;
        }
      `}
    >
      {finalListCategory.map((category) => (
        <CategoryButton
          key={category.categoryId}
          id={category.categoryId}
          name={category.name}
          description={category.description}
          imageUrl={category.imageUrl}
          onClick={onChange}
        />
      ))}
    </div>
  );
};

const CategoryButton = ({ id, name, imageUrl, description, onClick }) => {
  const { palette } = useTheme();
  const handleOnClick = useCallback(() => {
    onClick(id);
  }, [id, onClick]);
  return (
    <button
      css={`
        flex-wrap: wrap;
        border: 1px solid ${palette.grey[100]};
        border-radius: 10px;
        padding: 20px;
        display: flex;
        align-items: center;
        text-align: left;
        cursor: pointer;
        background: none;
        transition: border-color 0.05s ease-out;
        :hover {
          border-color: ${palette.primary.main};
          color: ${palette.primary.main};
        }
      `}
      onClick={handleOnClick}
    >
      <div
        css={`
          margin-right: 10px;
          width: 24px;
          height: 24px;
        `}
      >
        <img
          css={`
            width: 100%;
            height: auto;
          `}
          src={imageUrl}
          aria-hidden
        />
      </div>
      <div
        css={`
          flex: 1;
          text-align: left;
          line-height: 22px;
          font-size: 14px;
          font-weight: 700;
        `}
      >
        {name}
      </div>
      <div
        css={`
          margin-top: 10px;
          width: 100%;
          line-height: 20px;
          font-size: 12px;
          color: ${palette.grey[500]};
        `}
      >
        {description}
      </div>
    </button>
  );
};

const WritePost = ({ postId, categoryId, onBack, onSubmit, onClose }) => {
  const { palette, breakpoints } = useTheme();
  const formRef = useRef<HTMLFormElement>(null);
  const {
    state: { dataListCommunityCategory },
    doSetHasPostedInSession,
  } = useCommunity();
  const category = useMemo(
    () =>
      dataListCommunityCategory.find((c) => c.categoryId === categoryId) ||
      null,
    [categoryId, dataListCommunityCategory],
  );
  const { setValue, control, errors, formState, getValues, handleSubmit } =
    useFormContext<{
      title: string;
      contents: string;
    }>();

  /**
   * Appends a hashtag character to contents and sets focus on the field.
   */
  const handleOnHashtagClick = useCallback(() => {
    const { contents } = getValues();
    if (!contents.endsWith("#")) {
      const space = contents.match(/(^|\s)$/) ? "" : " ";
      const newContents = contents + space + "#";
      setValue("contents", newContents, { shouldValidate: true });
    }
    formRef.current?.contents?.focus();
  }, [getValues, setValue]);

  const handleOnSubmit = handleSubmit(
    async (data: { categoryId: number; title: string; contents: string }) => {
      try {
        await onSubmit({
          postId,
          categoryId: data.categoryId,
          title: data.title,
          contents: data.contents,
          isWithTimeline: isWithTimeline ? true : undefined,
        });
        doSetHasPostedInSession();
        onClose();
      } catch (e) {
        // noop
      }
    },
  );

  /**
   * Events related
   */
  const { tag } = useEvent();

  // view event
  useEffect(() => {
    if (tag) {
      tag(CustomEventLabel.ViewCommunityPostCreate);
    }
  }, [tag]);

  const { data: dataTimeline } = useDataTimeline();
  const [isWithTimeline, setIsWithTimeline] = useState(false);

  const handleOnToggleAttachTimeline = useCallback((bool: boolean) => {
    if (bool) {
      return attach;
    } else {
      return detach;
    }

    function detach(e: React.MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) {
      e.stopPropagation();
      setIsWithTimeline(false);
    }

    function attach() {
      setIsWithTimeline(true);
    }
  }, []);

  const [isTooltipShown, setIsTooltipShown] = useState(false);

  /**
   * Show timeline tooltip 1s after modal is mounted
   */
  useEffect(() => {
    setTimeout(() => setIsTooltipShown(true), 1000);
  }, []);

  const attachRef = useRef();

  const handleOnDismissTooltip = useCallback(
    () => setIsTooltipShown(false),
    [],
  );

  return (
    <form
      css={`
        ${breakpoints.down("sm")} {
          margin: 0 -10px;
        }
      `}
      onSubmit={handleOnSubmit}
      ref={formRef}
    >
      <StyledModalContent>
        <Stack spacing="20px">
          <button
            css={`
              padding: 14px 16px;
              width: 100%;
              height: 50px;
              border: 1px solid ${palette.grey[100]};
              border-radius: 10px;
              display: flex;
              text-align: left;
              line-height: 22px;
              font-size: 14px;
              background: none;
              cursor: pointer;
              transition: border-color 0.05s ease-out;
              :hover {
                border-color: ${palette.primary.main};
              }
            `}
            onClick={onBack}
          >
            <div
              css={`
                flex: 1;
              `}
            >
              {category?.name}
            </div>
            <div>
              <IconArrowDown />
            </div>
          </button>
          <Controller
            as={TextField}
            control={control}
            name="title"
            type="text"
            placeholder="Subject"
            defaultValue={getValues().contents}
            fullWidth
            autoFocus
            rules={{ required: true }}
            error={!!errors.title}
            helperText={errors.title?.message}
          />
          <Controller
            as={TextField}
            control={control}
            name="contents"
            type="text"
            placeholder="Share your concerns or experiences."
            multiline
            minRows={8}
            maxRows={18}
            fullWidth
            rules={{ required: true }}
            error={!!errors.contents}
            helperText={errors.contents?.message}
          />
          <div
            css={`
              margin: 0 -20px 20px;
              width: auto;
              display: flex;
              overflow-x: auto;
              overflow-y: visible;
              height: 70px;
              padding: 0 20px;
              -webkit-overflow-scrolling: touch; /* Lets it scroll lazy */
            `}
          >
            <HashtagButton
              size="small"
              onClick={handleOnHashtagClick}
              neutral
              css={`
                flex-shrink: 0;
              `}
            >
              <IconHashtag />
              Add Hashtag
            </HashtagButton>
            <div
              css={`
                position: relative;
              `}
            >
              <>
                {/* timeline tooltip is shown when the user doesn't have timeline data */}
                <Fade in={!dataTimeline && isTooltipShown} unmountOnExit>
                  <div
                    css={`
                      position: absolute;
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      top: 20px;
                      margin-left: -40px;
                      z-index: 1;
                      user-select: none;
                    `}
                  >
                    {/* arrow tip */}
                    <div
                      css={`
                        width: 12px;
                        height: 12px;
                        position: relative;
                        top: 8px;
                        background-color: #00b4ff;
                        transform: rotate(45deg);
                        z-index: 1;
                      `}
                    />
                    {/* tooltip */}
                    <div
                      css={`
                        background: #00b4ff;
                        border-radius: 10px;
                        font-weight: 500;
                        font-size: 12px;
                        line-height: 18px;
                        text-align: center;
                        color: #ffffff;
                        padding: 6px 8px;
                        z-index: 1;
                        white-space: nowrap;
                      `}
                      onClick={handleOnDismissTooltip}
                    >
                      📲 Create your timeline with the Lawfully App
                    </div>
                  </div>
                </Fade>
                <AttachButton
                  ref={attachRef}
                  size="small"
                  neutral
                  disabled={!dataTimeline}
                  isAttached={isWithTimeline}
                  onClick={handleOnToggleAttachTimeline(true)}
                >
                  <IconTimeline
                    css={`
                      ${dataTimeline && isWithTimeline
                        ? ""
                        : `
                  filter: grayscale(100%);
                `}
                    `}
                  />
                  {isWithTimeline
                    ? dataTimeline?.categoryName
                    : "Attach My Timeline"}
                  {isWithTimeline && (
                    <IconDelete
                      onClick={handleOnToggleAttachTimeline(false)}
                      css={`
                        margin-left: 10px;
                        fill: #195dee;
                      `}
                    />
                  )}
                </AttachButton>
              </>
            </div>
          </div>
        </Stack>
      </StyledModalContent>
      <StyledModalActions>
        <div
          css={`
            width: 160px;
            ${breakpoints.down("sm")} {
              width: 100%;
            }
          `}
        >
          <Button type="submit" disabled={formState.isSubmitting} round>
            Post
          </Button>
        </div>
      </StyledModalActions>
    </form>
  );
};

const Loading = () => {
  return (
    <div
      css={`
        display: flex;
        align-items: center;
        justify-content: center;
        height: 250px;
      `}
    >
      <Spin />
    </div>
  );
};

const HashtagButton = styled(Button)`
  &&& {
    width: auto;
    margin-right: 12px;
    height: 40px;
    color: ${({ theme }) => theme.palette.grey[500]};
    .button-label-root {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    svg {
      margin-right: 4px;
    }
  }
`;

const AttachButton = styled(HashtagButton)<{ isAttached?: boolean }>`
  &&& {
    margin-right: 20px;
    ${({ isAttached }) =>
      isAttached
        ? `
        color: #195DEE;
        background-color: #E3ECFF;

        :focus {
          background-color: #e3ecff;
        }`
        : ""}

    :disabled {
      background: #f3f4f8;
      color: #dddddd;

      svg {
        opacity: 0.2;
      }
    }
  }
`;

const StyledModalContent = styled(ModalContent)`
  &&& {
    padding: 0;
    overflow: visible;
    ${(props) => `${props.theme.breakpoints.down("sm")} {
      padding: 0;
    }`}
  }
`;

const StyledModalActions = styled(ModalActions)`
  &&& {
    padding: 0;
  }
`;

const Header = ({ step, onClose }) => {
  const { breakpoints, palette } = useTheme();

  return (
    <div
      css={`
        margin: -20px -30px 20px;
        padding: 20px 30px 26px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        line-height: 24px;
        font-size: 16px;
        font-weight: 700;
        border-bottom: 1px solid ${palette.grey[100]};
        ${breakpoints.down("sm")} {
          position: relative;
          justify-content: center;
          border: none;
        }
      `}
    >
      <div>{step === 0 ? "Select Category" : "Create Post"}</div>
      <button
        css={`
          margin-bottom: -5px;
          border: none;
          background: none;
          cursor: pointer;
          ${breakpoints.down("sm")} {
            position: absolute;
            left: 0;
            text-align: center;
            width: 60px;
            height: 60px;
          }
        `}
        type="button"
        onClick={onClose}
      >
        <IconClose />
      </button>
    </div>
  );
};
